import { supabaseClient } from "@/lib/supabase/client";
import type { Database } from "@revelate/types";
import type { SupabaseClient } from "@supabase/supabase-js";
import type {
  ExpectedCloseV2CompanyForecast,
  ExpectedCloseV2LocalForecastArtifact,
} from "./localExpectedCloseForecast";

type ExpectedCloseV2RpcArtifact = {
  schemaVersion: "expected-close-v2-company-forecast-v1";
  status: "ready" | "stale" | "unavailable";
  companyId: number;
  asOf?: string;
  generatedAt?: string;
  modelVersion?: string;
  horizonWeeks?: number;
  thresholdAppliedToCurve?: boolean;
  openDeals?: number;
  supportedDeals?: number;
  matchedSupportedDeals?: number;
  dealForecasts?: ExpectedCloseV2CompanyForecast["dealForecasts"];
  terminalBuyerOutcomeDeals?: ExpectedCloseV2CompanyForecast["terminalBuyerOutcomeDeals"];
};

type ExpectedCloseV2Database = {
  __InternalSupabase: Database["__InternalSupabase"];
  public: {
    CompositeTypes: Database["public"]["CompositeTypes"];
    Enums: Database["public"]["Enums"];
    Functions: Database["public"]["Functions"] & {
      get_expected_close_v2_forecast: {
        Args: { target_company_id: number };
        Returns: ExpectedCloseV2RpcArtifact;
      };
    };
    Tables: Database["public"]["Tables"];
    Views: Database["public"]["Views"];
  };
};

function isLocalhost() {
  return import.meta.env.DEV
    && typeof window !== "undefined"
    && ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
}

function normalizeRpcArtifact(
  artifact: ExpectedCloseV2RpcArtifact,
  companyId: number,
): ExpectedCloseV2CompanyForecast {
  if (
    artifact.schemaVersion !== "expected-close-v2-company-forecast-v1"
    || Number(artifact.companyId) !== companyId
    || artifact.status !== "ready"
    || artifact.horizonWeeks !== 53
    || !Array.isArray(artifact.dealForecasts)
  ) {
    throw new Error(`Expected Close v2 is ${artifact.status ?? "unavailable"}.`);
  }
  return {
    companyId,
    openDeals: Number(artifact.openDeals ?? 0),
    supportedDeals: Number(artifact.supportedDeals ?? 0),
    matchedSupportedDeals: Number(artifact.matchedSupportedDeals ?? 0),
    dealForecasts: artifact.dealForecasts,
    terminalBuyerOutcomeDeals: Array.isArray(artifact.terminalBuyerOutcomeDeals)
      ? artifact.terminalBuyerOutcomeDeals
      : [],
  };
}

async function getLocalFallback(companyId: number) {
  const response = await fetch(`/expected-close-v2/${encodeURIComponent(String(companyId))}.json`);
  if (!response.ok) throw new Error(`Expected Close v2 returned ${response.status}`);
  const artifact = await response.json() as ExpectedCloseV2LocalForecastArtifact;
  if (
    artifact.schemaVersion !== "expected-close-v2-local-company-forecast-v3"
    || String(artifact.company.companyId) !== String(companyId)
  ) {
    throw new Error("Expected Close v2 Deal contract is unavailable.");
  }
  return artifact.company;
}

export async function getExpectedCloseV2CompanyForecast(companyId: number) {
  const client = supabaseClient as SupabaseClient<ExpectedCloseV2Database>;
  const { data, error } = await client.rpc("get_expected_close_v2_forecast", {
    target_company_id: companyId,
  });
  if (!error && data) return normalizeRpcArtifact(data, companyId);
  if (isLocalhost()) return getLocalFallback(companyId);
  throw error ?? new Error("Expected Close v2 is unavailable.");
}
